import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, Building2, ChevronDown, ChevronLeft, ChevronRight, Compass, Cpu,
  Flag, Globe, Handshake, HardHat, Layers, MapPinned, Merge, Sparkles,
  TrendingUp, Users,
} from 'lucide-react';
import { getTimeline, TIMELINE_SECTION } from '@/data/about';
import { useLiveTick } from '@/lib/liveContent';
import { SectionHeader } from '../SectionHeader';
import { SlashMark } from '../AmdenMark';
import { cn } from '@/lib/utils';

// Professional line icons only — never emoji. Keys are managed in the CMS.
const TIMELINE_ICONS = {
  flag: Flag,
  building: Building2,
  users: Users,
  handshake: Handshake,
  trending: TrendingUp,
  hardhat: HardHat,
  globe: Globe,
  map: MapPinned,
  merge: Merge,
  sparkles: Sparkles,
  layers: Layers,
  compass: Compass,
  cpu: Cpu,
};

// Brand palette: navy leads, gold marks key points, category colours restrained.
const CATEGORY_STYLES = {
  Foundation: { color: '#0B1729', soft: 'rgba(11,23,41,0.07)' },
  Growth: { color: '#029E88', soft: 'rgba(2,158,136,0.10)' },
  People: { color: '#D6318B', soft: 'rgba(214,49,139,0.10)' },
  Expansion: { color: '#4225C4', soft: 'rgba(66,37,196,0.08)' },
  Transformation: { color: '#9C7400', soft: 'rgba(255,184,0,0.16)' },
  Systems: { color: '#24406B', soft: 'rgba(36,64,107,0.10)' },
};

const EASE = [0.22, 1, 0.36, 1];

// Mount only the tree matching the current viewport — keeps testids unique
// and prevents assistive tech from seeing both timeline variants at once.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

const iconFor = (milestone) => TIMELINE_ICONS[milestone.icon] || Flag;

const CategoryChip = ({ category }) => {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.Foundation;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
      style={{ color: style.color, backgroundColor: style.soft }}
      data-testid={`timeline-category-${(category || '').toLowerCase()}`}
    >
      {category}
    </span>
  );
};

// Larger detail panel shared by desktop (tab panel) and mobile (expander).
const MilestoneDetail = ({ milestone, siblings, onSelect, testid }) => {
  const Icon = iconFor(milestone);
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-6 shadow-card sm:p-8">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy-900 text-gold-500">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-xs font-medium tracking-widest text-gold-700">{milestone.displayDate}</p>
          <h3 className="mt-1 text-lg font-extrabold tracking-tight text-navy-900 sm:text-xl" data-testid="timeline-detail-title">
            {milestone.title}
          </h3>
        </div>
        <span className="ml-auto"><CategoryChip category={milestone.category} /></span>
      </div>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">{milestone.description}</p>
      {milestone.image?.src && (
        <img
          src={milestone.image.src}
          alt={milestone.image.alt || ''}
          loading="lazy"
          className="mt-5 aspect-[16/9] w-full max-w-md rounded-lg border border-slate-200 object-cover"
        />
      )}
      {siblings.length > 0 && (
        <div className="mt-5 rounded-lg border border-gold-200 bg-gold-50 p-4" data-testid="timeline-group-note">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gold-700">
            <Merge className="h-3.5 w-3.5" aria-hidden="true" />
            Two milestones, one transformation
          </p>
          <ul className="mt-2 space-y-1.5">
            {siblings.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelect(s.id)}
                  data-testid={`timeline-group-link-${s.id}`}
                  className="inline-flex items-center gap-1.5 rounded text-sm font-bold text-navy-900 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
                >
                  {s.title} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {milestone.link && (
        <Link
          to={milestone.link}
          data-testid="timeline-detail-link"
          className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-navy-900 transition-colors hover:text-gold-700"
        >
          Find out more
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
};

const TimelineEmpty = () => (
  <div
    data-testid="timeline-empty"
    className="relative mt-12 overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-white/70 px-6 py-12 text-center"
  >
    <SlashMark
      className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 opacity-[0.05]"
      bar="#0B1729"
      dot="#FFB800"
    />
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Being finalised</p>
    <h3 className="mt-3 text-lg font-extrabold tracking-tight text-navy-900 sm:text-xl">
      Our company milestones are being carefully put together.
    </h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
      We’d rather share the real story than a polished guess — verified Amden Group milestones will
      appear here soon.
    </p>
  </div>
);

const TimelineInteractive = ({ milestones }) => {
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  const total = milestones.length;

  const years = useMemo(() => [...new Set(milestones.map((m) => m.year))], [milestones]);

  const [activeId, setActiveId] = useState(
    () => (milestones.find((m) => m.featured) || milestones[0]).id
  );

  const activeIndex = Math.max(0, milestones.findIndex((m) => m.id === activeId));
  const active = milestones[activeIndex];
  const activeYear = active.year;
  const yearMilestones = milestones.filter((m) => m.year === activeYear);

  const select = (id) => setActiveId(id);
  const selectIndex = (i) => setActiveId(milestones[Math.min(Math.max(i, 0), total - 1)].id);
  const selectYear = (year) => setActiveId(milestones.find((m) => m.year === year).id);

  const siblingsOf = (m) =>
    m.groupKey ? milestones.filter((x) => x.groupKey === m.groupKey && x.id !== m.id) : [];

  // Roving-tabindex tabs pattern: arrow keys move between milestones.
  const onTrackKeyDown = (e) => {
    let next = null;
    if (e.key === 'ArrowRight') next = activeIndex + 1;
    else if (e.key === 'ArrowLeft') next = activeIndex - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = total - 1;
    else return;
    e.preventDefault();
    const clamped = Math.min(Math.max(next, 0), total - 1);
    selectIndex(clamped);
    // The target node may belong to another year — focus it after re-render.
    const targetId = milestones[clamped].id;
    requestAnimationFrame(() => document.getElementById(`timeline-node-${targetId}`)?.focus());
  };

  // Consecutive same-group runs inside the active year get a gold connector,
  // showing the December 2025 milestones as two parts of one transformation.
  const groupRuns = [];
  for (let i = 0; i < yearMilestones.length; i += 1) {
    const g = yearMilestones[i].groupKey;
    if (!g) continue;
    let j = i;
    while (j + 1 < yearMilestones.length && yearMilestones[j + 1].groupKey === g) j += 1;
    if (j > i) groupRuns.push({ key: g, from: i, to: j });
    i = j;
  }

  return (
    <>
      <p className="sr-only" aria-live="polite" data-testid="timeline-announcer">
        {`Milestone ${activeIndex + 1} of ${total}: ${active.displayDate} — ${active.title}`}
      </p>

      {/* ── Desktop / tablet: year-grouped stepped timeline ──────────── */}
      {isDesktop && (
      <div className="mt-14" data-testid="timeline-desktop">
        {/* Prominent year navigation */}
        <div className="flex flex-wrap items-end justify-center gap-x-10 gap-y-4" role="group" aria-label="Jump to a year" data-testid="timeline-years">
          {years.map((year) => {
            const isActive = year === activeYear;
            const count = milestones.filter((m) => m.year === year).length;
            return (
              <button
                key={year}
                type="button"
                aria-pressed={isActive}
                aria-label={`Show milestones from ${year}`}
                data-testid={`timeline-year-${year}`}
                onClick={() => selectYear(year)}
                className={cn(
                  'group flex flex-col items-center gap-1 rounded-md px-2 pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                  'border-b-[3px] transition-[border-color,color] duration-300',
                  isActive ? 'border-gold-500' : 'border-transparent hover:border-navy-200'
                )}
              >
                <span
                  className={cn(
                    'text-2xl font-extrabold tracking-tight transition-colors duration-300 lg:text-3xl',
                    isActive ? 'text-navy-900' : 'text-slate-300 group-hover:text-navy-400'
                  )}
                >
                  {year}
                </span>
                <span className={cn('text-[11px] font-bold uppercase tracking-widest', isActive ? 'text-gold-700' : 'text-slate-400')}>
                  {count} milestone{count > 1 ? 's' : ''}
                </span>
              </button>
            );
          })}
        </div>

        {/* Stepped track for the active year */}
        <div className="relative mx-auto mt-12 max-w-4xl">
          <ol
            role="tablist"
            aria-label={`Milestones in ${activeYear}`}
            onKeyDown={onTrackKeyDown}
            className="relative flex items-start"
            data-testid="timeline-track"
          >
            <span className="absolute left-0 right-0 top-6 h-0.5 bg-slate-200" aria-hidden="true" />
            {groupRuns.map((run) => (
              <span
                key={run.key}
                aria-hidden="true"
                className="absolute top-6 z-10 h-0.5 bg-gold-500"
                style={{
                  left: `${((run.from + 0.5) / yearMilestones.length) * 100}%`,
                  width: `${((run.to - run.from) / yearMilestones.length) * 100}%`,
                }}
              />
            ))}
            {yearMilestones.map((m) => {
              const globalIndex = milestones.findIndex((x) => x.id === m.id);
              const state = globalIndex < activeIndex ? 'past' : m.id === activeId ? 'selected' : 'future';
              const Icon = iconFor(m);
              return (
                <li key={m.id} className="relative flex flex-1 flex-col items-center px-2">
                  <button
                    type="button"
                    role="tab"
                    id={`timeline-node-${m.id}`}
                    aria-selected={state === 'selected'}
                    aria-controls="timeline-panel"
                    tabIndex={state === 'selected' ? 0 : -1}
                    data-testid={`timeline-node-${m.id}`}
                    onClick={() => select(m.id)}
                    className="group flex flex-col items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2"
                  >
                    <span
                      className={cn(
                        'relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 bg-white transition-[background-color,border-color,color,box-shadow,transform] duration-300',
                        state === 'selected' && 'scale-110 border-navy-900 bg-navy-900 text-gold-500 shadow-[0_0_0_6px_rgba(255,184,0,0.25)]',
                        state === 'past' && 'border-navy-900 bg-navy-900 text-white',
                        state === 'future' && 'border-slate-300 text-slate-400 group-hover:border-navy-300 group-hover:text-navy-600'
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col items-center gap-0.5 text-center">
                      <span
                        className={cn(
                          'font-mono text-xs font-medium tracking-widest transition-colors',
                          state === 'selected' ? 'text-gold-700' : 'text-slate-400'
                        )}
                      >
                        {m.displayDate.replace(` ${m.year}`, '')}
                      </span>
                      <span
                        className={cn(
                          'max-w-[11rem] text-xs font-bold leading-snug transition-colors',
                          state === 'selected' ? 'text-navy-900' : 'text-slate-500 group-hover:text-navy-700'
                        )}
                      >
                        {m.title}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          {groupRuns.length > 0 && (
            <p className="mt-4 text-center text-[11px] font-bold uppercase tracking-widest text-gold-700" data-testid="timeline-group-caption">
              Gold link — two milestones, one transformation
            </p>
          )}
        </div>

        {/* Detail panel */}
        <div
          id="timeline-panel"
          role="tabpanel"
          aria-labelledby={`timeline-node-${active.id}`}
          className="mx-auto mt-10 max-w-3xl"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <MilestoneDetail milestone={active} siblings={siblingsOf(active)} onSelect={select} testid="timeline-detail" />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Previous / next controls */}
        <div className="mx-auto mt-8 flex max-w-3xl items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => selectIndex(activeIndex - 1)}
            disabled={activeIndex === 0}
            data-testid="timeline-prev"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-navy-900 transition-colors hover:border-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous
          </button>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400" data-testid="timeline-position">
            Milestone {activeIndex + 1} of {total}
            <span className="ml-3 hidden font-medium normal-case tracking-normal text-slate-400 lg:inline">Use ← → arrow keys</span>
          </p>
          <button
            type="button"
            onClick={() => selectIndex(activeIndex + 1)}
            disabled={activeIndex === total - 1}
            data-testid="timeline-next"
            className="inline-flex items-center gap-2 rounded-md bg-navy-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      )}

      {/* ── Mobile: vertical journey with expanding details ──────────── */}
      {!isDesktop && (
      <div className="mt-12 space-y-10" data-testid="timeline-mobile">
        {years.map((year) => (
          <div key={year}>
            <h3 className="flex items-center gap-3 text-xl font-extrabold tracking-tight text-navy-900" data-testid={`timeline-m-year-${year}`}>
              <span className="h-2.5 w-2.5 rounded-full bg-gold-500" aria-hidden="true" />
              {year}
            </h3>
            <ol className="relative ml-1.5 mt-4 space-y-3 border-l-2 border-slate-200 pl-5">
              {milestones.filter((m) => m.year === year).map((m) => {
                const isActive = m.id === activeId;
                return (
                  <li key={m.id} className="relative">
                    <span
                      className={cn(
                        'absolute -left-[1.95rem] top-5 h-3 w-3 rounded-full border-2 transition-colors',
                        isActive ? 'border-navy-900 bg-gold-500' : 'border-slate-300 bg-white'
                      )}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      aria-expanded={isActive}
                      aria-controls={`timeline-m-detail-${m.id}`}
                      data-testid={`timeline-m-node-${m.id}`}
                      onClick={() => select(m.id)}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                        isActive ? 'border-navy-900 bg-white shadow-card' : 'border-slate-200 bg-white/70'
                      )}
                    >
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs font-medium tracking-widest text-gold-700">{m.displayDate}</span>
                          <CategoryChip category={m.category} />
                        </span>
                        <span className="mt-1.5 block text-base font-bold text-navy-900">{m.title}</span>
                      </span>
                      <ChevronDown
                        className={cn('h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300', isActive && 'rotate-180 text-navy-900')}
                        aria-hidden="true"
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          id={`timeline-m-detail-${m.id}`}
                          initial={reduce ? false : { opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={reduce ? undefined : { opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <div className="pt-3">
                            <MilestoneDetail milestone={m} siblings={siblingsOf(m)} onSelect={select} testid={`timeline-m-detail-${m.id}-content`} />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
      )}
    </>
  );
};

export const Timeline = () => {
  useLiveTick();
  const milestones = getTimeline();

  return (
    <section aria-labelledby="timeline-heading" data-testid="timeline-section" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03"
          eyebrow={TIMELINE_SECTION.eyebrow}
          title={TIMELINE_SECTION.heading}
          description={TIMELINE_SECTION.description}
          testid="timeline-header"
        />
        {milestones.length > 0 ? <TimelineInteractive milestones={milestones} /> : <TimelineEmpty />}
      </div>
    </section>
  );
};
